/**
 * Save the S2 item set as a reusable Item Pool.
 *
 * WHY (2026-09-02). The engine's Input Scope picker has always been able to
 * CONSUME an item pool (`listItemPools` in planningService), but a session that
 * had just assembled the right twenty items had no way to PRODUCE one. The set
 * lived in the session's v3 config and nowhere else, so the work of narrowing a
 * pool could not be handed to planning, reused by a second session, or scoped
 * against by the engine — it had to be reassembled by hand every time.
 *
 * WHAT IS AND IS NOT SAVED. `item_pools.priority_ids` is `uuid[]`: it addresses
 * real platform items. Items typed or imported directly into a session have
 * prototype ids and are not records the rest of the platform can resolve, so
 * they cannot go into a pool. They are reported back by name rather than
 * dropped quietly — a pool that silently contains fewer items than the room
 * agreed on is worse than one that says what it left behind.
 *
 * Reuses itemPoolsStore.createItemPool (permission checks, governance event,
 * Supabase insert) and addPrioritiesToList (one write, membership reconciled).
 * Nothing here is a second write path.
 */

import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useUserStore } from '../../stores/prioritization/userStore';
import type { EngineItem, SessionEvent } from './types';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A session item is poolable when it addresses a real platform record. */
export function isPoolableItem(item: Pick<EngineItem, 'id' | 'source'>): boolean {
  return item.source === 'platform' && UUID_RE.test(item.id);
}

export interface ItemPoolSavePlan {
  poolable: EngineItem[];
  /** Items that cannot be addressed by a pool, with the reason stated once. */
  excluded: EngineItem[];
}

/** What a save would do, computed before anyone commits to it. */
export function planItemPoolSave(items: readonly EngineItem[]): ItemPoolSavePlan {
  const poolable: EngineItem[] = [];
  const excluded: EngineItem[] = [];
  for (const item of items) (isPoolableItem(item) ? poolable : excluded).push(item);
  return { poolable, excluded };
}

/** Default name — the session's own title, which is what the room calls this set. */
export function defaultPoolName(event: Pick<SessionEvent, 'title'>): string {
  const base = event.title?.trim();
  return base ? `${base} — item pool` : 'Session item pool';
}

export interface SaveItemPoolResult {
  poolId: string;
  savedCount: number;
  excludedCount: number;
}

/**
 * Create the pool and populate it, in the session's declared order.
 *
 * Order matters: the pool's `priority_ids` is an ordered array, and S2's order
 * is the room's, not an accident.
 */
export async function saveSessionItemsAsPool(
  event: SessionEvent,
  name: string,
): Promise<SaveItemPoolResult> {
  const { poolable, excluded } = planItemPoolSave(event.items);
  if (poolable.length === 0) {
    throw new Error(
      'None of these items are platform records yet, so there is nothing a pool can address. Add items from the platform, or promote the typed ones to items first.',
    );
  }

  const ownerId = useUserStore.getState().userId ?? '';
  const store = useItemPoolsStore.getState();

  const poolId = await store.createItemPool({
    name: name.trim() || defaultPoolName(event),
    description: `Saved from session “${event.title}”.`,
    ownerId,
    comparisonSetId: '',
    criteriaIds: [],
    cutLinePosition: 0,
    status: 'draft',
    tags: ['session'],
    // Scope the pool the way the session is scoped, and no further. ItemPool
    // types trackId as required ("pools are always scoped to a track"), but the
    // column is nullable and the store supports standalone pools outright — a
    // session that is not track-linked produces one, and inventing a track id
    // to satisfy a stale type would put a false scope on a real record.
    ...(event.trackId ? { trackId: event.trackId } : {}),
    ...(event.cycleId ? { cycleId: event.cycleId } : {}),
  } as unknown as Parameters<typeof store.createItemPool>[0]);

  const { error } = useItemPoolsStore
    .getState()
    .addPrioritiesToList(
      poolId,
      poolable.map((i) => i.id),
      event.cycleId,
      ownerId || undefined,
      `Saved from session “${event.title}”`,
      'manual',
    );
  if (error) throw new Error(error);

  return { poolId, savedCount: poolable.length, excludedCount: excluded.length };
}
